import { defineStore } from 'pinia';
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import type { Client, ClientQuickLink, OnboardingRun, Project, Task, TaskClientResponseStatus } from '~/types/models';
import { PORTAL_DATA_MAX_AGE_MS, type FreshRequestOptions } from '~/lib/requestFreshness';
import { buildClientPortalDataScopeKey, isCurrentClientPortalDataScope } from '~/lib/clientPortalDataScope';

type PortalDataSlice = 'summary' | 'projects' | 'tasks';
type PortalDataResponse = {
    onboardingRuns?: OnboardingRun[];
    projects?: Project[];
    clientQuickLinks?: ClientQuickLink[];
    tasks?: Task[];
};

function isPendingClientAction(task: Task): boolean {
    return isPendingClientTaskWork(task);
}

const portalRequestsInFlight = new Map<string, Promise<void>>();

export const useClientPortalDataStore = defineStore('clientPortalData', {
    state: () => ({
        onboardingRuns: [] as OnboardingRun[],
        projects: [] as Project[],
        clientQuickLinks: [] as ClientQuickLink[],
        tasks: [] as Task[],
        activeScopeKey: null as string | null,
        loadingSummary: false,
        loadingProjects: false,
        loadingTasks: false,
        summaryKey: null as string | null,
        projectsKey: null as string | null,
        tasksKey: null as string | null,
        summaryLoadedAt: 0,
        projectsLoadedAt: 0,
        tasksLoadedAt: 0,
        error: null as string | null,
    }),

    getters: {
        pendingProjectContracts: (state): Project[] => state.projects.filter(project => project.contract?.status === 'awaiting_client'),
        pendingClientActionTasks: (state): Task[] => state.tasks.filter(task => isPendingClientAction(task)),
        pendingClientActionCount(): number {
            return this.pendingClientActionTasks.length;
        },
    },

    actions: {
        _buildRequestKey(tenantId: string, clientId: string): string {
            const portalStore = useClientPortalStore();
            return buildClientPortalDataScopeKey({
                tenantId,
                clientId,
                previewClientId: portalStore.previewingAsClientId,
            });
        },

        _activateScope(requestKey: string): void {
            if (this.activeScopeKey === requestKey) return;
            this.clear();
            this.activeScopeKey = requestKey;
        },

        _isSliceFresh(requestKey: string, slice: PortalDataSlice): boolean {
            const now = Date.now();
            if (slice === 'summary') return this.summaryKey === requestKey && now - this.summaryLoadedAt < PORTAL_DATA_MAX_AGE_MS;
            if (slice === 'projects') return this.projectsKey === requestKey && now - this.projectsLoadedAt < PORTAL_DATA_MAX_AGE_MS;
            return this.tasksKey === requestKey && now - this.tasksLoadedAt < PORTAL_DATA_MAX_AGE_MS;
        },

        _setSliceLoading(includes: PortalDataSlice[], value: boolean): void {
            if (includes.includes('summary')) this.loadingSummary = value;
            if (includes.includes('projects')) this.loadingProjects = value;
            if (includes.includes('tasks')) this.loadingTasks = value;
        },

        _clearSlices(includes: PortalDataSlice[]): void {
            if (includes.includes('summary')) {
                this.onboardingRuns = [];
                this.clientQuickLinks = [];
                this.summaryKey = null;
                this.summaryLoadedAt = 0;
            }
            if (includes.includes('projects')) {
                this.projects = [];
                this.projectsKey = null;
                this.projectsLoadedAt = 0;
            }
            if (includes.includes('tasks')) {
                this.tasks = [];
                this.tasksKey = null;
                this.tasksLoadedAt = 0;
            }
        },

        async _fetchPortalData(tenantId: string, clientId: string, includes: PortalDataSlice[]): Promise<PortalDataResponse> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const authUser = $auth.currentUser;

            if (!authUser) {
                throw new Error('You must be signed in to load client portal data.');
            }

            const token = await authUser.getIdToken();
            return await $fetch<PortalDataResponse>('/api/client-portal/data', {
                query: {
                    tenantId,
                    clientId,
                    include: includes.join(','),
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });
        },

        _applyPortalData(data: PortalDataResponse, includes: PortalDataSlice[], requestKey: string): void {
            const portalStore = useClientPortalStore();
            if (includes.includes('summary')) {
                this.onboardingRuns = Array.isArray(data.onboardingRuns) ? data.onboardingRuns : [];
                this.clientQuickLinks = Array.isArray(data.clientQuickLinks) ? data.clientQuickLinks : [];
                this.summaryKey = requestKey;
                this.summaryLoadedAt = Date.now();
            }
            if (includes.includes('projects')) {
                this.projects = hasClientPortalPermission(portalStore.currentAccess, 'viewProjects') && Array.isArray(data.projects)
                    ? data.projects
                    : [];
                this.projectsKey = requestKey;
                this.projectsLoadedAt = Date.now();
            }
            if (includes.includes('tasks')) {
                this.tasks = hasClientPortalPermission(portalStore.currentAccess, 'viewTasks') && Array.isArray(data.tasks)
                    ? data.tasks
                    : [];
                this.tasksKey = requestKey;
                this.tasksLoadedAt = Date.now();
            }
        },

        async fetchSlices(
            tenantId: string,
            clientId: string,
            includes: PortalDataSlice[],
            options: FreshRequestOptions = {},
        ): Promise<void> {
            const normalizedIncludes = Array.from(new Set(includes));
            const requestKey = this._buildRequestKey(tenantId, clientId);
            this._activateScope(requestKey);
            if (normalizedIncludes.length === 0) {
                return;
            }

            const existingRequest = portalRequestsInFlight.get(requestKey);
            if (existingRequest) {
                await existingRequest;
            }

            const slicesToFetch = options.force
                ? normalizedIncludes
                : normalizedIncludes.filter(slice => !this._isSliceFresh(requestKey, slice));
            if (slicesToFetch.length === 0) return;

            this._setSliceLoading(slicesToFetch, true);
            this.error = null;
            const request = (async () => {
                const response = await this._fetchPortalData(tenantId, clientId, slicesToFetch);
                if (!isCurrentClientPortalDataScope(this.activeScopeKey, requestKey)) return;
                this._applyPortalData(
                    response,
                    slicesToFetch,
                    requestKey,
                );
            })();
            portalRequestsInFlight.set(requestKey, request);
            try {
                await request;
            } catch (error) {
                if (!isCurrentClientPortalDataScope(this.activeScopeKey, requestKey)) return;
                this.error = `Error fetching client portal data: ${error}`;
                console.error(this.error);
                if (!slicesToFetch.every(slice => this._isSliceFresh(requestKey, slice))) {
                    this._clearSlices(slicesToFetch);
                }
            } finally {
                if (portalRequestsInFlight.get(requestKey) === request) {
                    portalRequestsInFlight.delete(requestKey);
                }
                if (isCurrentClientPortalDataScope(this.activeScopeKey, requestKey)) {
                    this._setSliceLoading(slicesToFetch, false);
                }
            }
        },

        clear(): void {
            this.onboardingRuns = [];
            this.projects = [];
            this.clientQuickLinks = [];
            this.tasks = [];
            this.loadingSummary = false;
            this.loadingProjects = false;
            this.loadingTasks = false;
            this.summaryKey = null;
            this.projectsKey = null;
            this.tasksKey = null;
            this.summaryLoadedAt = 0;
            this.projectsLoadedAt = 0;
            this.tasksLoadedAt = 0;
            this.activeScopeKey = null;
            this.error = null;
        },

        async fetchProjects(tenantId: string, clientId: string, options: FreshRequestOptions = {}): Promise<void> {
            await this.fetchSlices(tenantId, clientId, ['projects'], options);
        },

        async fetchTasks(tenantId: string, clientId: string, options: FreshRequestOptions = {}): Promise<void> {
            await this.fetchSlices(tenantId, clientId, ['tasks'], options);
        },

        async fetchSummary(tenantId: string, clientId: string, options: FreshRequestOptions = {}): Promise<void> {
            await this.fetchSlices(tenantId, clientId, ['summary'], options);
        },

        async initialize(tenantId: string, clientId: string, options: FreshRequestOptions = {}): Promise<void> {
            await this.fetchSlices(tenantId, clientId, ['summary', 'projects', 'tasks'], options);
        },

        async submitTaskClientResponse(params: {
            tenantId: string,
            taskId: string,
            responseText?: string,
            responseStatus?: TaskClientResponseStatus,
            responderId?: string,
            responderName?: string,
        }): Promise<void> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const clientId = portalStore.currentClient?.id || portalStore.currentAccess?.clientId || '';
            const authUser = $auth.currentUser;

            if (!hasClientPortalPermission(portalStore.currentAccess, 'viewTasks')) {
                throw new Error('You do not have permission to respond to tasks.');
            }

            if (!clientId || !authUser) {
                throw new Error('Client portal session is not ready.');
            }

            const existingTask = this.tasks.find(task => task.id === params.taskId);
            if (!existingTask) {
                throw new Error('Task not found.');
            }

            if (existingTask.clientVisible !== true || existingTask.clientResponseRequired !== true) {
                throw new Error('This task is not accepting client responses.');
            }

            const token = await authUser.getIdToken();
            const response = await $fetch<{ task: Task }>('/api/client-portal/tasks/respond', {
                method: 'POST',
                body: {
                    tenantId: params.tenantId,
                    clientId,
                    taskId: params.taskId,
                    responseText: String(params.responseText || '').trim(),
                    responseStatus: params.responseStatus || 'completed',
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            this.tasks = this.tasks.map((task) => (
                task.id === params.taskId
                    ? { ...task, ...response.task }
                    : task
            ));
        },

        async signContractForProject(
            tenantId: string,
            projectId: string,
            signer: { signerId?: string, signerName?: string, signerEmail?: string },
        ): Promise<void> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const clientId = portalStore.currentClient?.id || portalStore.currentAccess?.clientId || '';
            const authUser = $auth.currentUser;
            const currentProject = this.projects.find(project => project.id === projectId);

            if (!currentProject?.contract) {
                throw new Error('No contract is available for this project.');
            }

            if (!clientId || !authUser) {
                throw new Error('Client portal session is not ready.');
            }

            const token = await authUser.getIdToken();
            const response = await $fetch<{ project: Project }>('/api/client-portal/contracts/sign', {
                method: 'POST',
                body: {
                    tenantId,
                    clientId,
                    projectId,
                    scope: 'project',
                    signerName: String(signer.signerName || '').trim(),
                    acceptanceConfirmed: true,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            this.projects = this.projects.map((project) => (
                project.id === projectId
                    ? { ...project, ...response.project }
                    : project
            ));
        },

        async signContractForClient(
            tenantId: string,
            clientId: string,
            signer: { signerId?: string, signerName?: string, signerEmail?: string },
        ): Promise<void> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const authUser = $auth.currentUser;
            const currentClient = portalStore.currentClient as (Client & { tenantId?: string }) | null;

            if (!currentClient?.contract) {
                throw new Error('No client agreement is available.');
            }

            if (!authUser) {
                throw new Error('Client portal session is not ready.');
            }

            const token = await authUser.getIdToken();
            const response = await $fetch<{ client: Client }>('/api/client-portal/contracts/sign', {
                method: 'POST',
                body: {
                    tenantId,
                    clientId,
                    scope: 'client',
                    signerName: String(signer.signerName || '').trim(),
                    acceptanceConfirmed: true,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            portalStore.currentClient = {
                ...currentClient,
                ...response.client,
            } as typeof portalStore.currentClient;
            portalStore.accessibleClients = portalStore.accessibleClients.map((client) => (
                client.id === clientId && client.tenantId === tenantId
                    ? { ...client, ...response.client }
                    : client
            ));
        },
    },
});
